import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from '../services/api.js';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [wsState, setWsState] = useState('disconnected');
  const [toasts, setToasts] = useState([]);
  
  const { isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const pendingMarkAsRead = useRef(new Set()); // Для отслеживания уведомлений в процессе отметки

  // Загрузка уведомлений и статистики
  const loadNotifications = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const [notificationsData, statsData] = await Promise.all([
        notificationsAPI.getNotifications(params),
        notificationsAPI.getStats()
      ]);
      
      // Обрабатываем данные уведомлений
      const processedNotifications = (notificationsData || []).map(notification => ({
        ...notification,
        notification_type: notification.notification_type || notification.type || 'info',
        status: notification.status || 'unread',
        title: notification.title || 'Уведомление',
        message: notification.message || '',
        created_at: notification.created_at || new Date().toISOString(),
        read_at: notification.read_at || null
      }));
      
      setNotifications(processedNotifications);
      
      // Устанавливаем счетчик из статистики, а не вычисляем локально
      // Это предотвращает рассинхронизацию
      setUnreadCount(statsData.unread || 0);
      
      return { success: true, notifications: processedNotifications, stats: statsData };
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Не удалось загрузить уведомления');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Вспомогательная функция для вычисления локального счетчика
  const calculateLocalUnreadCount = useCallback((notifs) => {
    return notifs.filter(notif => notif.status === 'unread').length;
  }, []);

  // Обработка сообщений от WebSocket
  const handleWebSocketMessage = useCallback((message) => {
    console.log('📨 WebSocket message received:', message);
    
    switch (message.type) {
      case 'new_notification':
        handleNewNotification(message.data);
        break;
        
      case 'notification_read':
        handleNotificationRead(message.data);
        break;
        
      case 'all_notifications_read':
        handleAllNotificationsRead();
        break;
        
      case 'pong':
        console.log('💓 WebSocket heartbeat received');
        break;
        
      case 'connection_established':
        console.log('✅ WebSocket connection confirmed');
        break;
        
      default:
        console.log('📨 Unknown WebSocket message type:', message.type);
    }
  }, []);

  // Обработка нового уведомления
  const handleNewNotification = useCallback((notificationData) => {
    const newNotification = {
      ...notificationData,
      notification_type: notificationData.notification_type || notificationData.type || 'info',
      status: 'unread'
    };
    
    // Добавляем уведомление в начало списка
    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      // Обновляем счетчик на основе актуальных данных
      setUnreadCount(calculateLocalUnreadCount(updated));
      return updated;
    });
    
    // Показываем toast уведомление
    showToast({
      id: Date.now() + Math.random(),
      title: newNotification.title,
      message: newNotification.message,
      type: newNotification.notification_type,
      notificationId: newNotification.id
    });
  }, [calculateLocalUnreadCount]);

  // Обработка отметки уведомления как прочитанного (от WebSocket)
  const handleNotificationRead = useCallback((data) => {
    setNotifications(prev => {
      const updated = prev.map(notif => 
        notif.id === data.notification_id 
          ? { ...notif, status: 'read', read_at: data.read_at }
          : notif
      );
      // Обновляем счетчик на основе актуальных данных
      setUnreadCount(calculateLocalUnreadCount(updated));
      return updated;
    });
  }, [calculateLocalUnreadCount]);

  // Обработка отметки всех уведомлений как прочитанных
  const handleAllNotificationsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notif => ({
        ...notif,
        status: 'read',
        read_at: notif.read_at || new Date().toISOString()
      }))
    );
    setUnreadCount(0);
  }, []);

  // Функция отключения WebSocket
  const disconnectWebSocket = useCallback(() => {
    console.log('🔌 Disconnecting WebSocket...');
    
    // Очищаем интервалы и таймауты
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Закрываем WebSocket соединение
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    
    setIsWsConnected(false);
    setWsState('disconnected');
  }, []);

  // Функция подключения WebSocket
  const connectWebSocket = useCallback(() => {
    // Закрываем существующее соединение
    disconnectWebSocket();
    
    const token = localStorage.getItem('access_token');
    if (!token || !isAuthenticated) {
      console.log('❌ No token or not authenticated, skipping WebSocket connection');
      setWsState('disconnected');
      return;
    }

    const API_BASE_URL = window.location.hostname.includes('localhost') 
      ? 'ws://localhost:8080'
      : `wss://${window.location.hostname}`;

    const wsUrl = `${API_BASE_URL}/api/v1/notifications/ws?token=${token}`;
    
    console.log('🔌 Connecting to WebSocket:', wsUrl);
    setWsState('connecting');
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        setIsWsConnected(true);
        setWsState('connected');
        
        // Запускаем heartbeat для поддержания соединения
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };
      
      ws.onclose = (event) => {
        console.log('📤 WebSocket disconnected:', event.code, event.reason);
        setIsWsConnected(false);
        setWsState('disconnected');
        
        // Очищаем heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Автоматическое переподключение только если пользователь авторизован
        if (isAuthenticated && event.code !== 1000) {
          console.log('🔄 Attempting to reconnect in 5 seconds...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setIsWsConnected(false);
        setWsState('disconnected');
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
    } catch (error) {
      console.error('❌ WebSocket connection error:', error);
      setIsWsConnected(false);
      setWsState('disconnected');
    }
  }, [isAuthenticated, handleWebSocketMessage, disconnectWebSocket]);

  // Отметить уведомление как прочитанное
  const markAsRead = async (notificationId) => {
    // Защита от повторных кликов
    if (pendingMarkAsRead.current.has(notificationId)) {
      return { success: false, error: 'Уже в процессе отметки' };
    }

    pendingMarkAsRead.current.add(notificationId);

    try {
      setError('');
      
      // Оптимистичное обновление UI
      setNotifications(prev => {
        const updated = prev.map(notif => 
          notif.id === notificationId && notif.status === 'unread'
            ? { ...notif, status: 'read', read_at: new Date().toISOString() }
            : notif
        );
        // Обновляем счетчик на основе актуальных данных
        setUnreadCount(calculateLocalUnreadCount(updated));
        return updated;
      });

      const result = await notificationsAPI.markAsRead(notificationId);
      
      // Синхронизируем с серверными данными
      setNotifications(prev => {
        const updated = prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, status: 'read', read_at: result.read_at || new Date().toISOString() }
            : notif
        );
        // Двойная проверка счетчика
        setUnreadCount(calculateLocalUnreadCount(updated));
        return updated;
      });
      
      return { success: true, notification: result };
    } catch (err) {
      console.error('Error marking notification as read:', err);
      
      // Откатываем изменения при ошибке
      setNotifications(prev => {
        const updated = prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, status: 'unread', read_at: null }
            : notif
        );
        setUnreadCount(calculateLocalUnreadCount(updated));
        return updated;
      });
      
      setError('Не удалось отметить уведомление как прочитанное');
      return { success: false, error: err.message };
    } finally {
      pendingMarkAsRead.current.delete(notificationId);
    }
  };

  // Отметить все уведомления как прочитанные
  const markAllAsRead = async () => {
    try {
      setError('');
      
      // Оптимистичное обновление
      setNotifications(prev => 
        prev.map(notif => ({
          ...notif,
          status: 'read',
          read_at: notif.read_at || new Date().toISOString()
        }))
      );
      setUnreadCount(0);

      const result = await notificationsAPI.markAllAsRead();
      
      // Синхронизируем с сервером
      await loadNotifications();
      
      return { success: true, result };
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      
      // Откатываем при ошибке
      await loadNotifications();
      
      setError('Не удалось отметить все уведомления как прочитанные');
      return { success: false, error: err.message };
    }
  };

  // Показать toast уведомление
  const showToast = (toastData) => {
    setToasts(prev => [...prev, toastData]);
    
    setTimeout(() => {
      removeToast(toastData.id);
    }, 8000);
  };

  // Удалить toast уведомление
  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  // Отправить тестовое уведомление
  const sendTestNotification = async (userId, title, message) => {
    try {
      const result = await notificationsAPI.testWebSocket(userId, title, message);
      return { success: true, result };
    } catch (err) {
      console.error('Error sending test notification:', err);
      return { success: false, error: err.message };
    }
  };

  
  // Подключаем WebSocket при изменении состояния аутентификации TODO: Временное отключение
  // useEffect(() => {
  //   if (isAuthenticated) {
  //     console.log('🔌 User authenticated, connecting WebSocket...');
  //     loadNotifications();
  //     connectWebSocket();
  //   } else {
  //     console.log('🔌 User not authenticated, disconnecting WebSocket...');
  //     disconnectWebSocket();
  //     setNotifications([]);
  //     setUnreadCount(0);
  //     setToasts([]);
  //   }

  //   return () => {
  //     disconnectWebSocket();
  //   };
  // }, [isAuthenticated, connectWebSocket, disconnectWebSocket, loadNotifications]);

  const value = {
    // Состояние
    notifications,
    unreadCount,
    loading,
    error,
    isWsConnected,
    wsState,
    toasts,
    
    // Действия
    loadNotifications,
    markAsRead,
    markAllAsRead,
    sendTestNotification,
    removeToast,
    
    // WebSocket функции
    connectWebSocket,
    disconnectWebSocket,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};